import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Subscribe to Postgres changes on one or more tables (scoped to a business)
 * and call `onChange` after a short debounce. Used to keep list/calendar
 * views live without wiring realtime into every hook by hand.
 *
 * Tables must be in the `supabase_realtime` publication for events to arrive.
 */
export function useBusinessTableChanges(
  businessId: string | null | undefined,
  tables: readonly string[],
  onChange: () => void,
  options: { enabled?: boolean; debounceMs?: number } = {},
) {
  const { enabled = true, debounceMs = 400 } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const tablesKey = tables.join('|');

  useEffect(() => {
    if (!enabled || !businessId || tables.length === 0) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        onChangeRef.current();
      }, debounceMs);
    };

    let channel = supabase.channel(`biz-${businessId}-${tablesKey.replace(/[^a-z0-9_|]/gi, '')}`);
    for (const table of tables) {
      channel = channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table, filter: `business_id=eq.${businessId}` },
        schedule,
      );
    }
    channel.subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, tablesKey, enabled, debounceMs]);
}
